import { useState } from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';

const FinalCTA = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d763dakbmgf2o8mm70s0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enroll"
      className="relative py-24 px-6 bg-charcoal overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e73b61c7099c08ceffe28c79fdba2148.jpg"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-charcoal/85" />
      </div>

      {/* Gold accent lines */}
      <div className="absolute left-0 top-0 w-full h-[2px] bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute left-0 bottom-0 w-full h-[2px] bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div ref={ref} className="reveal relative z-10 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">
              Start Today
            </span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Start Your Training With a Team Built for Results
          </h2>
          <p className="font-inter text-base text-white/60 max-w-xl mx-auto">
            Enrollment is open. Let&apos;s build a training plan around your goals, your timeline, and your future.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <a
            href="tel:4698535038"
            className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line" />
            Call 469-853-5038
          </a>
          <a
            href="#enroll-form"
            className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Complete Enrollment Form
            <i className="ri-arrow-down-line" />
          </a>
        </div>

        {/* Form */}
        {submitted ? (
          <div className="bg-white/5 border border-gold/30 rounded-lg p-10 text-center" id="enroll-form">
            <div className="w-14 h-14 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-gold text-2xl" />
            </div>
            <p className="font-playfair text-2xl font-bold text-white mb-2">Enrollment Request Received</p>
            <p className="font-inter text-sm text-white/60">
              Our team will reach out within one business day to discuss your program and get you started.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="enroll-form"
            onSubmit={handleSubmit}
            className="bg-white/5 border border-white/10 rounded-lg p-8 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block font-inter text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Full Name *
                </label>
                <input
                  required
                  type="text"
                  name="name"
                  placeholder="Your full name"
                  className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/30 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block font-inter text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Phone Number *
                </label>
                <input
                  required
                  type="tel"
                  name="phone"
                  placeholder="Your phone number"
                  className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/30 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block font-inter text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Email Address *
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/30 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block font-inter text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Program of Interest
                </label>
                <select
                  name="program"
                  className="w-full bg-white/5 border border-white/15 text-white/80 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200 cursor-pointer"
                >
                  <option value="" className="bg-charcoal">Select a program</option>
                  <option value="Vocational Skills Training" className="bg-charcoal">Vocational Skills Training</option>
                  <option value="Job Readiness & Career Development" className="bg-charcoal">Job Readiness &amp; Career Development</option>
                  <option value="Life Skills Development" className="bg-charcoal">Life Skills Development</option>
                  <option value="Employment Placement Support" className="bg-charcoal">Employment Placement Support</option>
                  <option value="Community Reintegration" className="bg-charcoal">Community Reintegration</option>
                  <option value="Workforce Development" className="bg-charcoal">Workforce Development</option>
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold hover:bg-gold-dark disabled:opacity-60 text-charcoal font-inter font-bold text-sm py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Submitting...' : 'Submit Enrollment Request'}
            </button>
            <p className="font-inter text-xs text-white/30 text-center mt-4">
              Confidential &bull; No obligation &bull; Fast response
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default FinalCTA;
